/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */

import { cssMap, cx, jsx } from '@atlaskit/css';
import { type TriggerProps } from '@atlaskit/popup';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import Suspense from '@atlaskit/react-ufo/suspense';
import { token } from '@atlaskit/tokens';

import { HoverCard } from '../../hover-card/HoverCard';
import { getBorderColor, getIconColor } from '../skill-colors';
import { SkillHoverCardContent } from '../skill-hover-card-content/SkillHoverCardContent';
import { type SkillIconComponent, skillIconMap } from '../skill-icons';
import type { Skill } from '../types';

export type RovoSkillTagProps = Skill & {
	// invert colors for use with dark backgrounds
	invertColors?: boolean;
	// disable hovercard functionality
	disableHovercard?: boolean;
};

const styles = cssMap({
	parallelogram: {
		marginLeft: token('space.050'),
		paddingLeft: token('space.075'),
		paddingRight: token('space.075'),
		borderRadius: token('radius.xsmall'),
		// to create a parallelogram, skew the span -15 degrees
		transform: 'skewX(-15deg)',
		display: 'inline-block',
	},
	background: {
		backgroundColor: token('color.background.neutral'),
	},
	backgroundInverse: {
		backgroundColor: token('color.background.inverse.subtle'),
	},
	content: {
		// skew the contents of the span back to normal orientation so that only the outer element is skewed
		transform: 'skewX(15deg)',
	},
});

const SkillTagInternal = ({
	triggerProps = {},
	invertColors,
	slug,
	IconComponent,
	onMouseEnter,
	onMouseLeave,
	color,
}: {
	triggerProps?: Partial<TriggerProps>;
	onMouseEnter?: React.MouseEventHandler;
	onMouseLeave?: React.MouseEventHandler;
	IconComponent?: SkillIconComponent;
} & Omit<RovoSkillTagProps, 'iconName'>) => {
	const borderColor = getBorderColor(color, invertColors);
	return (
		<Box
			as="span"
			xcss={cx(styles.parallelogram, invertColors ? styles.backgroundInverse : styles.background)}
			style={{
				borderLeft: `${token('border.width.selected')} solid ${borderColor}`,
			}}
			onMouseEnter={onMouseEnter}
			onMouseLeave={onMouseLeave}
			{...triggerProps}
		>
			<Inline xcss={styles.content} space="space.050" alignBlock="center">
				{IconComponent && (
					<Suspense fallback={null} interactionName="skill-tag-icon">
						<IconComponent label="" size="small" color={getIconColor(color, invertColors)} />
					</Suspense>
				)}
				{slug}
			</Inline>
		</Box>
	);
};

// delay opening and closing of the hovercard to prevent flickering
const SKILL_TAG_HOVERCARD_DELAY = 200;

export const SkillTag = ({
	invertColors = false,
	disableHovercard = false,
	product,
	iconName,
	...props
}: RovoSkillTagProps) => {
	const IconComponent = iconName ? skillIconMap[iconName] : undefined;
	const hasHovercardInfo = props?.name && props?.description;

	return disableHovercard || !hasHovercardInfo ? (
		<SkillTagInternal
			{...props}
			invertColors={invertColors}
			disableHovercard={disableHovercard}
			IconComponent={IconComponent}
		/>
	) : (
		<HoverCard
			placement="top"
			fallbackPlacements={['bottom', 'left']}
			autoFocus={false}
			hoverCardDelay={SKILL_TAG_HOVERCARD_DELAY}
			content={<SkillHoverCardContent product={product} iconName={iconName} {...props} />}
		>
			<SkillTagInternal
				invertColors={invertColors}
				disableHovercard={disableHovercard}
				IconComponent={IconComponent}
				{...props}
			/>
		</HoverCard>
	);
};
